import { useEffect, useState, useContext, useCallback } from "react";
import { remisionService } from "@services/RemisionService";
import { createHeader, getNumberCurrency } from "@utils/utils";
import { INewProduct, IProduct, IRemisionDetail, IRemisionProductSub, IUpdateRemision } from "src/types/global";
import { useNavigate, useLocation } from "react-router-dom";
import { GlobalAlertContext } from "@providers/GlobalAlertProvider";
import RemisionReturnsUI from "./RemisionReturnsUI";
import { useForm } from "react-hook-form";
interface IFormValues {
  applicant: string;
  employeeCode: string;
}
const RemisionReturns = () => {
  const [remision, setRemision] = useState<IRemisionDetail>(({
    total: 0
  } as IRemisionDetail));
  const [requestProducts, setRequestProducts] = useState<IProduct[]>([]);
  const [products, setProducts] = useState<INewProduct[]>([]);
  const [rows, setRows] = useState<INewProduct[]>([]);
  const [pages, setPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [productError, setProductError] = useState(false);
  const [showError, setShowError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showEmployeeDialog, setShowEmployeeDialog] = useState(false);
  const rowsPerPage = 6;
  const {
    setGlobalAlertMessage,
    setGlobalAlertSeverity,
    showGlobalAlert
  } = useContext(GlobalAlertContext);
  const navigate = useNavigate();
  const location = useLocation();
  const {
    register,
    handleSubmit,
    reset,
    watch
  } = useForm<IFormValues>({
    defaultValues: {
      applicant: "",
      employeeCode: ""
    }
  });
  const getTotalValue = () => products.map(p => p.totalPrice).reduce((total, currentValue) => {
    const currentTotal = currentValue ? parseFloat(currentValue.replaceAll(".", "")) : 0;
    return total + currentTotal;
  }, 0);
  const onSubmit = async (data: IFormValues) => {
    try {
      if (products.length === 0) return setProductError(true);
      if (!data.employeeCode) throw new Error("El código de empleado es requerido");
      setIsLoading(true);
      setGlobalAlertSeverity("success");
      setGlobalAlertMessage("Productos devueltos");
      const payload: IUpdateRemision = {
        operation: 1,
        employeeCode: data.employeeCode,
        applicant: data.applicant,
        remisionId: Number(remision.id),
        products: products.map(prod => {
          const localProd = {
            ...prod
          };
          localProd.price = getNumberCurrency(String(localProd.price));
          delete localProd.totalPrice;
          delete localProd.actions;
          return {
            ...localProd
          };
        })
      };
      await remisionService.put(payload);
      navigate(`/remisiones/${remision.id}`, {
        state: {
          showAlert: true
        }
      });
      showGlobalAlert();
      handleCloseDialog();
    } catch {
      setShowError(true);
    } finally {
      setIsLoading(false);
    }
  };
  const handleShowDialog = () => {
    if (products.length === 0) return setProductError(true);
    setShowEmployeeDialog(true);
  };
  const handleCloseDialog = () => {
    setShowEmployeeDialog(false);
    reset({
      applicant: "",
      employeeCode: ""
    });
  };
  const getRemision = useCallback(async (id: number) => {
    try {
      setIsLoading(true);
      const res = await remisionService.get(id);
      setRemision(res.data);
      setRequestProducts(res.data.requestProducts.map((prod: IRemisionProductSub) => ({
        id: prod.productId,
        reference: prod.productReference,
        description: prod.productName,
        price: prod.price,
        status: 0
      })));
    } catch {
      setShowError(true);
    } finally {
      setIsLoading(false);
    }
  }, []);
  useEffect(() => {
    const id = Number(location.pathname.split("/").reverse()[1]);
    const routeState: IRemisionDetail = location.state;
    if (id === Number(routeState?.id)) {
      setRemision(routeState);
      setRequestProducts((routeState.requestProducts ?? []).map((prod: IRemisionProductSub) => ({
        id: prod.productId,
        reference: prod.productReference,
        description: prod.productName,
        price: prod.price,
        status: 0
      })));
      return;
    }
    getRemision(id);
  }, [getRemision, location]);
  useEffect(() => {
    setRows([...products.slice(rowsPerPage * (currentPage - 1), rowsPerPage * currentPage)]);
  }, [products, currentPage]);
  useEffect(() => {
    setPages(Math.ceil(products.length / rowsPerPage));
  }, [products]);
  const headers = [createHeader("Producto", "productName"), createHeader("Cantidad", "quantity"), createHeader("Precio unitario", "price"), createHeader("Total", "totalPrice"), createHeader("Acciones", "actions")];
  return <RemisionReturnsUI headers={headers} rows={rows} pages={pages} onChangePage={setCurrentPage} isLoading={isLoading} remision={remision} products={products} setProducts={setProducts} getTotalValue={getTotalValue} onSubmit={handleShowDialog} showError={showError} closeError={() => setShowError(false)} productError={productError} closeProductError={() => setProductError(false)} onCancel={() => navigate(-1)} query={(rowsPerPage: number, search: string) => requestProducts.filter(p => p.description.toLowerCase().includes(search.toLowerCase()))} showEmployeeDialog={showEmployeeDialog} employeeCode={watch("employeeCode")} onEmployeeCodeChange={register("employeeCode")} onConfirmSubmit={handleSubmit(onSubmit)} onCancelDialog={handleCloseDialog} applicant={watch("applicant")} onApplicantChange={register("applicant")} />;
};
export default RemisionReturns;